export type CanvasDatabasePresentationKind = 'invalid' | 'live' | 'summary';

export type CanvasDatabaseTransformState = 'active' | 'idle';

export function shouldRunCanvasDatabasePresentationWork(input: {
  storageReady: boolean;
  transformState: CanvasDatabaseTransformState;
}): boolean {
  return input.storageReady && input.transformState === 'active';
}

export function shouldScheduleCanvasDatabasePresentationSync(
  changedProperties: Iterable<PropertyKey>
): boolean {
  const properties = [...changedProperties];
  return (
    properties.length === 0 ||
    properties.some(
      property => property !== '_selected' && property !== '_editing'
    )
  );
}

export type CanvasDatabasePresentationReadinessInput = {
  storageReady: boolean;
  presentationKind?: CanvasDatabasePresentationKind;
  invalidReason?: string;
  liveViewLogicRequired?: boolean;
  liveViewLogicReady?: boolean;
  error?: unknown;
};

export type CanvasDatabasePresentationReadiness =
  | {
      status: 'loading';
    }
  | {
      status: 'ready';
    }
  | {
      reason: string;
      status: 'unavailable';
    }
  | {
      reason: string;
      status: 'error';
    };

function formatErrorReason(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error) {
    return error;
  }
  return 'Database view failed to load.';
}

export function resolveCanvasDatabasePresentationReadiness(
  input: CanvasDatabasePresentationReadinessInput
): CanvasDatabasePresentationReadiness {
  if (input.error) {
    return {
      reason: formatErrorReason(input.error),
      status: 'error',
    };
  }

  if (!input.storageReady || !input.presentationKind) {
    return { status: 'loading' };
  }

  if (input.presentationKind === 'invalid') {
    return {
      reason: input.invalidReason || 'Database canvas presentation is invalid.',
      status: 'unavailable',
    };
  }

  if (
    input.presentationKind === 'live' &&
    input.liveViewLogicRequired &&
    !input.liveViewLogicReady
  ) {
    return { status: 'loading' };
  }

  return { status: 'ready' };
}
